import {
  getCompsVisibilityOptions,
  getCompsVisibilityQueryKey,
  updateCompsVisibilityMutation,
  useHeyApi,
} from "@platform/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  compsVisibilityStatesEqual,
  defaultHiddenRowGroupIds,
  deriveCompsHiddenColumnIds,
  toCompsVisibilityGridState,
} from "./buildCompsVisibilityGridState";

import type { CompsVisibilityState } from "./buildCompsVisibilityGridState";
import type { CompsGridRow } from "./types";
import type { MpxAgGridStateProps } from "@platform/hooks";
import type { GridState, StateUpdatedEvent } from "ag-grid-community";

const PERSIST_DEBOUNCE_MS = 500;

interface UseCompsVisibilityGridStateConfig {
  /** The ecosystem RFP id — the numeric route id is rejected by the BFF. */
  rfpId: string;
  /** Row data, used to derive the default hidden rows for an unconfigured RFP. */
  rowData: CompsGridRow[];
  /** The mpx-backed grid props (order/width/sort) to layer visibility onto. */
  gridProps: MpxAgGridStateProps;
  /**
   * False while the mpx grid state is still loading. Seeding waits for it:
   * `gridProps.initialState` is undefined both while loading and when nothing
   * was ever saved, so seeding early would read "no legacy hidden columns",
   * persist `configured: true`, and permanently discard hides that were about
   * to arrive.
   */
  gridStateReady?: boolean;
  enabled?: boolean;
}

interface UseCompsVisibilityGridStateResult {
  gridProps: MpxAgGridStateProps;
  hiddenRowGroupIds: Set<string>;
  toggleRowGroup: (rowGroupId: string) => void;
  isLoading: boolean;
}

/**
 * Persists the Comps grid's hidden rows and columns to the shared, per-RFP
 * comps-visibility store, replacing the two per-user localStorage keys so every
 * analyst on the RFP — and the Marketing Presentation export — sees the same
 * hidden set.
 *
 * Columns live in AG-Grid's own state and are seeded through `initialState`.
 * Rows do not: the Coverages panel drives a custom external filter, so the
 * hidden-row set is returned as a plain `Set` for the grid to filter against
 * and is written back through `toggleRowGroup`. Both halves funnel into one
 * debounced write, because the store holds them in a single row.
 */
export const useCompsVisibilityGridState = ({
  rfpId,
  rowData,
  gridProps,
  gridStateReady = true,
  enabled = true,
}: UseCompsVisibilityGridStateConfig): UseCompsVisibilityGridStateResult => {
  const { centroPortalClient } = useHeyApi();
  const queryClient = useQueryClient();

  const queryKey = getCompsVisibilityQueryKey({
    path: { id: rfpId },
    client: centroPortalClient,
  });

  const { data, isLoading } = useQuery({
    ...getCompsVisibilityOptions({
      path: { id: rfpId },
      client: centroPortalClient,
    }),
    enabled: enabled && !!rfpId,
    staleTime: 5 * 60 * 1000,
  });

  const serverState = useMemo<CompsVisibilityState>(
    () => ({
      configured: data?.configured ?? false,
      hiddenRowGroupIds: data?.hiddenRowGroupIds ?? [],
      hiddenColumnIds: data?.hiddenColumnIds ?? [],
    }),
    [data],
  );

  const {
    initialState: serverInitialState,
    onStateUpdated: serverOnStateUpdated,
  } = gridProps;

  // Until this RFP has been configured, the hidden columns already carried by
  // the mpx grid state stand in — that is where they lived before this store
  // existed. Seeding adopts and persists them, after which the store is the
  // only source, which matters because mpx's write path is disabled and a
  // hide left there could never be cleared.
  const effectiveHiddenColumnIds = useMemo<string[]>(
    () =>
      serverState.configured
        ? serverState.hiddenColumnIds
        : (serverInitialState?.columnVisibility?.hiddenColIds ?? []),
    [serverState, serverInitialState],
  );

  const [hiddenRowGroupIds, setHiddenRowGroupIds] = useState<Set<string>>(
    new Set(),
  );

  // The current hidden halves, mirrored in refs so a write triggered by one of
  // them can read the other without either becoming a callback dependency.
  const hiddenRowGroupIdsRef = useRef<Set<string>>(hiddenRowGroupIds);
  const hiddenColumnIdsRef = useRef<string[]>(effectiveHiddenColumnIds);

  // Tracks the state of the currently scheduled/in-flight write (if any), kept
  // distinct from server truth so a pending or failed write is never mistaken
  // for persisted state.
  const pendingRef = useRef<CompsVisibilityState | undefined>(undefined);

  const { mutate } = useMutation({
    ...updateCompsVisibilityMutation({ client: centroPortalClient }),
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, response);
    },
    onSettled: () => {
      // Clear the in-flight target once the request finishes: on success the
      // query effect adopts the confirmed state as truth; on failure this frees
      // a later change to re-attempt the write.
      pendingRef.current = undefined;
    },
  });

  // Tracks the state already confirmed server-side so echoes of the seeded
  // state (and no-op state updates) never trigger a redundant PUT.
  const lastPersistedRef = useRef<CompsVisibilityState>(serverState);
  useEffect(() => {
    lastPersistedRef.current = serverState;
  }, [serverState]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const persist = useCallback(
    (
      hidden: Pick<
        CompsVisibilityState,
        "hiddenRowGroupIds" | "hiddenColumnIds"
      >,
    ) => {
      if (!enabled) return;

      // Any write the client makes is by definition a configured state.
      const next: CompsVisibilityState = { configured: true, ...hidden };

      // Back at server truth: cancel any scheduled write, nothing to persist.
      if (compsVisibilityStatesEqual(next, lastPersistedRef.current)) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
        pendingRef.current = undefined;
        return;
      }

      // Already scheduled/sent this exact state; don't stack a duplicate write.
      if (
        pendingRef.current &&
        compsVisibilityStatesEqual(next, pendingRef.current)
      ) {
        return;
      }

      pendingRef.current = next;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        mutate({ path: { id: rfpId }, body: next });
      }, PERSIST_DEBOUNCE_MS);
    },
    [enabled, mutate, rfpId],
  );

  // Seed the hidden rows once per RFP: from the store when it has been
  // configured, otherwise from the defaults, which are then persisted so the
  // next load reads server truth instead of re-deriving them.
  const seededRfpIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      !data ||
      !gridStateReady ||
      seededRfpIdRef.current === rfpId ||
      rowData.length === 0
    ) {
      return;
    }
    seededRfpIdRef.current = rfpId;

    // Read the columns from the current render rather than the ref, which was
    // captured on the first render — before the mpx grid state had loaded.
    hiddenColumnIdsRef.current = effectiveHiddenColumnIds;

    if (data.configured) {
      const seeded = new Set(data.hiddenRowGroupIds);
      hiddenRowGroupIdsRef.current = seeded;
      setHiddenRowGroupIds(seeded);
      return;
    }

    const defaults = defaultHiddenRowGroupIds(rowData);
    const seeded = new Set(defaults);
    hiddenRowGroupIdsRef.current = seeded;
    setHiddenRowGroupIds(seeded);
    persist({
      hiddenRowGroupIds: defaults,
      hiddenColumnIds: effectiveHiddenColumnIds,
    });
  }, [data, rfpId, rowData, persist, gridStateReady, effectiveHiddenColumnIds]);

  const toggleRowGroup = useCallback(
    (rowGroupId: string) => {
      const next = new Set(hiddenRowGroupIdsRef.current);
      if (next.has(rowGroupId)) {
        next.delete(rowGroupId);
      } else {
        next.add(rowGroupId);
      }

      hiddenRowGroupIdsRef.current = next;
      setHiddenRowGroupIds(next);
      persist({
        hiddenRowGroupIds: Array.from(next),
        hiddenColumnIds: hiddenColumnIdsRef.current,
      });
    },
    [persist],
  );

  const initialState = useMemo<GridState | undefined>(() => {
    const { columnVisibility } = toCompsVisibilityGridState(
      effectiveHiddenColumnIds,
    );

    if (!serverInitialState && !columnVisibility) {
      return undefined;
    }

    return { ...serverInitialState, columnVisibility };
  }, [serverInitialState, effectiveHiddenColumnIds]);

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      serverOnStateUpdated(event);

      // Track the grid's columns even while initializing, so a row toggle that
      // lands before any column change still writes the seeded columns back.
      hiddenColumnIdsRef.current = deriveCompsHiddenColumnIds(event.api);

      if (!enabled || event.sources?.includes("gridInitializing")) {
        return;
      }

      persist({
        hiddenRowGroupIds: Array.from(hiddenRowGroupIdsRef.current),
        hiddenColumnIds: hiddenColumnIdsRef.current,
      });
    },
    [serverOnStateUpdated, enabled, persist],
  );

  return {
    gridProps: { key: gridProps.key, initialState, onStateUpdated },
    hiddenRowGroupIds,
    toggleRowGroup,
    isLoading,
  };
};
